import React, { useRef, useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { readManualCrops, saveDetections, downloadDetectionExcel, findFileInMcp } from '../../services/analyses';
import DetectionMatchBlock from './DetectionMatchBlock';
import DetectionMatchEditor, { detectionIsAssignable } from './DetectionMatchEditor';
import { downloadFile } from '../../services/folder';
import AnalysisOperations from './AnalysisOperations';
import { applyOverridesToOM } from './overridesApplier';
import SimilarDrawingsPanel from './SimilarDrawingsPanel';
import './DetectionView.css';

const CLASS_COLORS = {
  Angle: [255, 255, 0],
  Centrality: [0, 200, 200],
  Chamfer: [0, 180, 180],
  Concentricity: [0, 128, 128],
  Cylindricity: [200, 200, 0],
  Datum: [100, 100, 100],
  Diameter: [255, 0, 0],
  Flatness: [0, 150, 150],
  GroupedRoughness: [255, 0, 180],
  Length: [0, 200, 0],
  Notes: [180, 140, 60],
  Parallelity: [255, 128, 0],
  Perpendicularity: [0, 128, 255],
  Position: [50, 100, 200],
  Radius: [0, 0, 255],
  Roughness: [255, 0, 255],
  Runout: [128, 128, 0],
  Straightness: [100, 200, 100],
  Symmetry: [100, 0, 200],
  Thread: [255, 165, 0],
  TotalRunout: [128, 200, 0],
  TruePosition: [200, 0, 128],
};

const getColor = (det) => {
  if (det.color) {
    const [b, g, r] = det.color;
    return `rgb(${r},${g},${b})`;
  }
  const rgb = CLASS_COLORS[det.class_name] || [200, 200, 200];
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
};

const getRgb = (className) => CLASS_COLORS[className] || [200, 200, 200];
const MEASUREMENT_CLASSES = Object.keys(CLASS_COLORS).filter(c => c !== 'Notes').sort();

const IconMoveArrows = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M8 1v14M1 8h14M8 1l-2 2M8 1l2 2M8 15l-2-2M8 15l2-2M1 8l2-2M1 8l2 2M15 8l-2-2M15 8l-2 2"/>
  </svg>
);

const IconRectDashed = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="3" width="12" height="10" rx="1" strokeDasharray="3 2"/>
  </svg>
);

const IconEye = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/>
    <circle cx="8" cy="8" r="2"/>
  </svg>
);

const IconEyeOff = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/>
    <line x1="3" y1="3" x2="13" y2="13"/>
  </svg>
);

const IconEraser = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 2l3 3-7 7-5 1 1-5zM9 4l3 3"/>
  </svg>
);

const IconFit = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M1 5V1h4M11 1h4v4M15 11v4h-4M5 15H1v-4"/>
  </svg>
);

const IconLink = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M7 9l2-2M5.5 8.5L3.7 10.3a2.1 2.1 0 003 3l1.8-1.8M10.5 7.5l1.8-1.8a2.1 2.1 0 00-3-3L7.5 4.5"/>
  </svg>
);

const formatMetaDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

const DetectionView = forwardRef(({ detections, imageSize, imageSrc, onClose, analysisHash, page = 1, analysisData, onNavigateToPreview, onViewSimilarAnalysis, onDetectAnnotations, detectingAnnotations = false, embeddedInPreview = false, onStateChange, operationsEnabled }, ref) => {
  const [activeTab, setActiveTab] = useState('annotations');
  const [analysisDataState, setAnalysisDataState] = useState(analysisData);
  const [showOnlyUnmatched, setShowOnlyUnmatched] = useState(false);
  const [editingDetectionIdx, setEditingDetectionIdx] = useState(null);
  const [editingMatchKey, setEditingMatchKey] = useState(null);

  const enabledOpsList = useMemo(() => {
    if (Array.isArray(operationsEnabled)) return operationsEnabled;
    if (operationsEnabled && typeof operationsEnabled === 'object') return Object.keys(operationsEnabled).filter((k) => operationsEnabled[k]);
    return null;
  }, [operationsEnabled]);

  const upsertMatchOverride = useCallback((idx, override, targetMatchKey) => {
    updateDetections((prev) => {
      const next = [...prev];
      const det = { ...next[idx] };
      const extras = [...(det.extras || [])];

      const overrideEntry = {
        target_op_path: targetMatchKey?.op_path ?? null,
        target_instance_idx: typeof targetMatchKey?.instance_idx === 'number' ? targetMatchKey.instance_idx : null,
        op_path: override.op_path,
        fields: override.fields || {},
      };
      const ovIdx = extras.findIndex((e) => e && e.label === '_match_override');
      let overrideList = [];
      if (ovIdx !== -1) {
        const raw = extras[ovIdx].value;
        overrideList = Array.isArray(raw) ? [...raw] : (raw ? [{ ...raw, target_op_path: null, target_instance_idx: null }] : []);
        const existsAt = overrideList.findIndex((o) =>
          (o.target_op_path ?? null) === (overrideEntry.target_op_path ?? null) &&
          (o.target_instance_idx ?? null) === (overrideEntry.target_instance_idx ?? null)
        );
        if (existsAt !== -1) overrideList[existsAt] = overrideEntry;
        else overrideList.push(overrideEntry);
        extras[ovIdx] = { label: '_match_override', value: overrideList };
      } else {
        overrideList = [overrideEntry];
        extras.push({ label: '_match_override', value: overrideList });
      }

      const matchesIdx = extras.findIndex((e) => e && e.label === '_matches');
      const currentMatches = (matchesIdx !== -1 && Array.isArray(extras[matchesIdx].value)) ? [...extras[matchesIdx].value] : [];
      const targetIdx = currentMatches.findIndex((m) =>
        m && m.op_path === overrideEntry.target_op_path &&
        (typeof m.instance_idx === 'number' ? m.instance_idx : null) === overrideEntry.target_instance_idx
      );
      const provisional = {
        op_path: override.op_path,
        instance_idx: typeof targetMatchKey?.instance_idx === 'number' ? targetMatchKey.instance_idx : 0,
        param: null,
        page: targetMatchKey?.page ?? null,
        _provisional: true,
        _override_fields: override.fields || {},
      };
      if (targetIdx !== -1) currentMatches[targetIdx] = provisional;
      else currentMatches.push(provisional);
      if (matchesIdx !== -1) extras[matchesIdx] = { label: '_matches', value: currentMatches };
      else extras.push({ label: '_matches', value: currentMatches });

      det.extras = extras;
      next[idx] = det;
      return next;
    });
    setEditingDetectionIdx(null);
    setEditingMatchKey(null);
  }, []);

  const removeMatchOverride = useCallback((idx, targetMatchKey = null) => {
    updateDetections((prev) => {
      const next = [...prev];
      const det = { ...next[idx] };
      const extras = [...(det.extras || [])];
      const ovIdx = extras.findIndex((e) => e && e.label === '_match_override');
      if (ovIdx === -1) {
        return prev;
      }
      const raw = extras[ovIdx].value;
      const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      if (!targetMatchKey) {
        extras.splice(ovIdx, 1);
      } else {
        const filtered = list.filter((o) =>
          !((o.target_op_path ?? null) === (targetMatchKey.op_path ?? null) &&
            (o.target_instance_idx ?? null) === (typeof targetMatchKey.instance_idx === 'number' ? targetMatchKey.instance_idx : null))
        );
        if (filtered.length === 0) extras.splice(ovIdx, 1);
        else extras[ovIdx] = { label: '_match_override', value: filtered };
      }
      det.extras = extras;
      next[idx] = det;
      return next;
    });
  }, []);

  const rejectMatch = useCallback((idx, match) => {
    updateDetections((prev) => {
      const next = [...prev];
      const det = { ...next[idx] };
      const extras = [...(det.extras || [])];
      const rejectedIdx = extras.findIndex((e) => e && e.label === '_match_rejected');
      const rejectedEntry = {
        op_path: match.op_path,
        instance_idx: typeof match.instance_idx === 'number' ? match.instance_idx : null,
      };
      let rejectedList = [];
      if (rejectedIdx !== -1) {
        rejectedList = Array.isArray(extras[rejectedIdx].value) ? [...extras[rejectedIdx].value] : [];
        const exists = rejectedList.some((r) => r.op_path === rejectedEntry.op_path && r.instance_idx === rejectedEntry.instance_idx);
        if (!exists) rejectedList.push(rejectedEntry);
        extras[rejectedIdx] = { label: '_match_rejected', value: rejectedList };
      } else {
        rejectedList = [rejectedEntry];
        extras.push({ label: '_match_rejected', value: rejectedList });
      }
      const currentMatches = (extras.find((e) => e && e.label === '_matches')?.value) || [];
      const newMatches = currentMatches.filter((m) => !(m.op_path === match.op_path && m.instance_idx === match.instance_idx));
      const matchesIdx = extras.findIndex((e) => e && e.label === '_matches');
      if (matchesIdx !== -1) extras[matchesIdx] = { label: '_matches', value: newMatches };
      det.extras = extras;
      next[idx] = det;
      return next;
    });
  }, []);

  useEffect(() => {
    setAnalysisDataState(analysisData);
  }, [analysisData]);

  const operacionesMecanizadoRaw = useMemo(() => {
    const full = analysisDataState?.result?.full_json;
    if (!full) return null;
    const parsed = typeof full === 'string' ? (() => { try { return JSON.parse(full); } catch { return null; } })() : full;
    if (!parsed || typeof parsed !== 'object') return null;
    const om = parsed.OperacionesMecanizado;
    return om && typeof om === 'object' ? om : null;
  }, [analysisDataState]);

  const imgRef = useRef(null);
  const svgRef = useRef(null);
  const panelRef = useRef(null);
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [editedDetections, setEditedDetections] = useState([]);

  const operacionesMecanizado = useMemo(() => {
    if (!operacionesMecanizadoRaw) return null;
    return applyOverridesToOM(operacionesMecanizadoRaw, editedDetections);
  }, [operacionesMecanizadoRaw, editedDetections]);

  const analysisDataWithOverrides = useMemo(() => {
    if (!analysisDataState || !operacionesMecanizado) return analysisDataState;
    const result = analysisDataState.result;
    if (!result) return analysisDataState;
    const fullJsonOrig = result.full_json;
    const fullJsonParsed = typeof fullJsonOrig === 'string'
      ? (() => { try { return JSON.parse(fullJsonOrig); } catch { return null; } })()
      : fullJsonOrig;
    if (!fullJsonParsed || typeof fullJsonParsed !== 'object') return analysisDataState;
    return {
      ...analysisDataState,
      result: {
        ...result,
        full_json: { ...fullJsonParsed, OperacionesMecanizado: operacionesMecanizado },
      },
    };
  }, [analysisDataState, operacionesMecanizado]);
  const [cropPreview, setCropPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [reportModal, setReportModal] = useState({ open: false, step: 0, done: false, error: false });
  const [showHelp, setShowHelp] = useState(false);
  const [mcpPath, setMcpPath] = useState(analysisData?.mcp_path || null);
  const [downloadSearching, setDownloadSearching] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(!onNavigateToPreview);

  const [activeTool, setActiveTool] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState(null);
  const [showDetections, setShowDetections] = useState(true);

  const [selectedClass, setSelectedClass] = useState('Diameter');
  const [pendingRects, setPendingRects] = useState([]);
  const [drawingRect, setDrawingRect] = useState(null);
  const [processingManual, setProcessingManual] = useState(false);

  const [pairingDiameterIdx, setPairingDiameterIdx] = useState(null);
  const [pairingLengthIndices, setPairingLengthIndices] = useState([]);

  const updateDetections = (updater) => {
    setEditedDetections(updater);
    setHasUnsavedChanges(true);
  };

  const drawingMode = activeTool === 'draw';
  const eraseMode = activeTool === 'erase';
  const pairMode = activeTool === 'pair';
  const fromDetectionHistory = !!onNavigateToPreview;

  const skipNextAutosaveRef = useRef(false);
  const editedDetectionsRef = useRef(editedDetections);
  editedDetectionsRef.current = editedDetections;
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const okStatusTimerRef = useRef(null);

  const performSave = useCallback(async () => {
    if (!analysisHash) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveStatus(null);
    try {
      const payload = editedDetectionsRef.current.map((det) => ({
        class_name: det.class_name,
        value: det.value || null,
        confidence: det.confidence,
        is_manual: det.is_manual || false,
        parse_error: det.parse_error || false,
        bbox_points: det.bbox_points,
        extras: det.extras || [],
      }));
      dirtyRef.current = false;
      await saveDetections(analysisHash, payload, page);
      if (!dirtyRef.current) {
        setHasUnsavedChanges(false);
        setSaveStatus('ok');
        if (okStatusTimerRef.current) clearTimeout(okStatusTimerRef.current);
        okStatusTimerRef.current = setTimeout(() => setSaveStatus(null), 2000);
      }
    } catch (err) {
      console.error('Error autosaving detections:', err);
      dirtyRef.current = true;
      setSaveStatus('error');
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (dirtyRef.current) {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => { performSave(); }, 800);
      }
    }
  }, [analysisHash, page]);

  useEffect(() => {
    skipNextAutosaveRef.current = true;
    setEditedDetections(detections.map((d) => {
      if (d.extras && d.extras.length > 0) return { ...d };
      const extras = [];
      if (d.tolerance !== undefined && d.tolerance !== null && d.tolerance !== '') {
        extras.push({ label: 'Tol.', value: String(d.tolerance) });
      }
      return { ...d, extras };
    }));
    setHasUnsavedChanges(false);
    dirtyRef.current = false;
  }, [detections]);

  useEffect(() => {
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    if (!analysisHash) return;
    dirtyRef.current = true;
    setHasUnsavedChanges(true);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => { performSave(); }, 800);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [editedDetections, analysisHash, performSave]);

  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current || savingRef.current) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const updateScale = useCallback(() => {
    if (!imgRef.current) return;
    const refW = imageSize?.width || imgRef.current.naturalWidth;
    const refH = imageSize?.height || imgRef.current.naturalHeight;
    if (!refW || !refH) return;
    setScale({
      x: imgRef.current.offsetWidth / refW,
      y: imgRef.current.offsetHeight / refH,
    });
  }, [imageSize]);

  useEffect(() => {
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [updateScale, imageSrc]);

  const handleFieldChange = (idx, field, value) => {
    updateDetections((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleAddExtra = (idx) => {
    updateDetections((prev) => {
      const next = [...prev];
      const det = next[idx];
      const extras = det.extras || [];
      const defaultLabel = '';
      next[idx] = { ...det, extras: [...extras, { label: defaultLabel, value: '' }] };
      return next;
    });
  };

  const handleExtraChange = (idx, extraIdx, field, value) => {
    updateDetections((prev) => {
      const next = [...prev];
      const extras = [...(next[idx].extras || [])];
      extras[extraIdx] = { ...extras[extraIdx], [field]: value };
      next[idx] = { ...next[idx], extras };
      return next;
    });
  };

  const handleRemoveExtra = (idx, extraIdx) => {
    updateDetections((prev) => {
      const next = [...prev];
      const extras = [...(next[idx].extras || [])];
      extras.splice(extraIdx, 1);
      next[idx] = { ...next[idx], extras };
      return next;
    });
  };

  const confirmPairing = useCallback(() => {
    if (pairingDiameterIdx === null || pairingLengthIndices.length === 0) return;
    updateDetections((prev) => {
      const next = [...prev];
      const det = { ...next[pairingDiameterIdx] };
      const extras = [...(det.extras || [])];
      const pairedIdx = extras.findIndex((e) => e.label === '_paired');
      const val = pairingLengthIndices.join(',');
      if (pairedIdx !== -1) {
        extras[pairedIdx] = { ...extras[pairedIdx], value: val };
      } else {
        extras.push({ label: '_paired', value: val });
      }
      next[pairingDiameterIdx] = { ...det, extras };
      return next;
    });
    setPairingDiameterIdx(null);
    setPairingLengthIndices([]);
  }, [pairingDiameterIdx, pairingLengthIndices]);

  const handlePairClick = (idx) => {
    const det = editedDetections[idx];
    if (!det) return;
    if (det.class_name === 'Diameter') {
      if (pairingDiameterIdx !== null && pairingLengthIndices.length > 0) {
        confirmPairing();
      }
      setPairingDiameterIdx(idx);
      setPairingLengthIndices([]);
    } else if (det.class_name === 'Length' && pairingDiameterIdx !== null) {
      setPairingLengthIndices((prev) =>
        prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
      );
    }
  };

  const getPairedLengthSum = (det) => {
    const paired = (det.extras || []).find((e) => e.label === '_paired');
    if (!paired || !paired.value) return null;
    const indices = paired.value.split(',').map(Number).filter((n) => !isNaN(n));
    let sum = 0;
    for (const i of indices) {
      const ld = editedDetections[i];
      if (ld) {
        const num = parseFloat((ld.value || '').replace(',', '.'));
        if (!isNaN(num)) sum += num;
      }
    }
    return sum > 0 ? sum : null;
  };

  const handleCropHover = (det) => {
    if (!imgRef.current) return;

    const natW = imgRef.current.naturalWidth;
    const natH = imgRef.current.naturalHeight;
    const refW = imageSize?.width || natW;
    const refH = imageSize?.height || natH;
    const rx = natW / refW;
    const ry = natH / refH;

    const xs = det.bbox_points.map(p => p[0] * rx);
    const ys = det.bbox_points.map(p => p[1] * ry);
    const bx = Math.min(...xs);
    const by = Math.min(...ys);
    const bw = Math.max(...xs) - bx;
    const bh = Math.max(...ys) - by;

    const pad = Math.max(bw, bh) * 0.25;
    const sx = Math.max(0, bx - pad);
    const sy = Math.max(0, by - pad);
    const sw = Math.min(natW - sx, bw + pad * 2);
    const sh = Math.min(natH - sy, bh + pad * 2);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const swap = rotation === 90 || rotation === 270;
    canvas.width = swap ? sh : sw;
    canvas.height = swap ? sw : sh;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(imgRef.current, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh);

    setCropPreview({ src: canvas.toDataURL(), color: getColor(det) });
  };

  // --- Zoom ---
  const handleZoomIn = () => setZoomLevel((z) => Math.min(4, z * 1.25));
  const handleZoomOut = () => setZoomLevel((z) => Math.max(0.25, z / 1.25));
  const handleFitToScreen = () => { setZoomLevel(1); setPanOffset({ x: 0, y: 0 }); };

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    setZoomLevel((z) => Math.min(4, Math.max(0.25, z * factor)));
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.addEventListener('wheel', handleWheel, { passive: false });
    return () => panel.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // --- Pan ---
  const handlePanStart = (e) => {
    const isMiddle = e.button === 1;
    const isLeftPan = activeTool === 'pan' && e.button === 0;
    if (!isMiddle && !isLeftPan) return;
    if (e.target.closest('.detection-toolbar') || e.target.closest('.detection-draw-options')) return;
    e.preventDefault();
    setIsPanning(true);
    setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
  };

  const handlePanMove = (e) => {
    if (!isPanning) return;
    setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  };

  const handlePanEnd = () => setIsPanning(false);

  // --- Drawing ---
  const getSvgPoint = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  const handleDrawStart = (e) => {
    if (!drawingMode || e.button !== 0) return;
    e.preventDefault();
    const p = getSvgPoint(e);
    setDrawingRect({ x1: p.x, y1: p.y, x2: p.x, y2: p.y, class_name: selectedClass });
  };

  const handleDrawMove = (e) => {
    if (!drawingRect) return;
    const p = getSvgPoint(e);
    setDrawingRect((prev) => ({ ...prev, x2: p.x, y2: p.y }));
  };

  const handleDrawEnd = () => {
    if (!drawingRect) return;
    const { x1, y1, x2, y2, class_name } = drawingRect;
    const w = Math.abs(x2 - x1);
    const h = Math.abs(y2 - y1);
    if (w >= 10 && h >= 10) {
      const minX = Math.min(x1, x2);
      const minY = Math.min(y1, y2);
      setPendingRects((prev) => [...prev, { x1: minX, y1: minY, x2: minX + w, y2: minY + h, class_name }]);
    }
    setDrawingRect(null);
  };

  const removePendingRect = (idx) => {
    setPendingRects((prev) => prev.filter((_, i) => i !== idx));
  };

  const extractCropB64 = (rect) => {
    const img = imgRef.current;
    if (!img) return null;
    const refW = imageSize?.width || img.naturalWidth;
    const refH = imageSize?.height || img.naturalHeight;
    const rx = img.naturalWidth / refW;
    const ry = img.naturalHeight / refH;

    const natX = (rect.x1 / scale.x) * rx;
    const natY = (rect.y1 / scale.y) * ry;
    const natW = ((rect.x2 - rect.x1) / scale.x) * rx;
    const natH = ((rect.y2 - rect.y1) / scale.y) * ry;

    const canvas = document.createElement('canvas');
    canvas.width = natW;
    canvas.height = natH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, natX, natY, natW, natH, 0, 0, natW, natH);
    return canvas.toDataURL('image/png').split(',')[1];
  };

  const handleProcessManual = async () => {
    if (!pendingRects.length) return;
    setProcessingManual(true);

    try {
      const crops = pendingRects.map((rect) => ({
        crop_b64: extractCropB64(rect),
        class_name: rect.class_name,
      }));

      const result = await readManualCrops(crops);
      const newDetections = result.detections.map((det, i) => {
        const rect = pendingRects[i];
        const x1 = rect.x1 / scale.x;
        const y1 = rect.y1 / scale.y;
        const x2 = rect.x2 / scale.x;
        const y2 = rect.y2 / scale.y;
        const extras = [];
        if (det.tolerance) extras.push({ label: 'Tol.', value: String(det.tolerance) });
        return {
          class_name: det.class || det.class_name || rect.class_name,
          value: det.value,
          confidence: null,
          is_manual: true,
          parse_error: det.parse_error || false,
          bbox_points: [[x1, y1], [x2, y1], [x2, y2], [x1, y2]],
          extras,
        };
      });

      updateDetections((prev) => [...prev, ...newDetections]);
      setPendingRects([]);
      setActiveTool(null);
    } catch (err) {
      console.error('Error processing manual crops:', err);
    } finally {
      setProcessingManual(false);
    }
  };

  const removeDetection = (idx) => {
    if (pairingDiameterIdx === idx) {
      setPairingDiameterIdx(null);
      setPairingLengthIndices([]);
    } else if (pairingDiameterIdx !== null && pairingDiameterIdx > idx) {
      setPairingDiameterIdx((prev) => prev - 1);
    }
    setPairingLengthIndices((prev) =>
      prev.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))
    );
    updateDetections((prev) => {
      const filtered = prev.filter((_, i) => i !== idx);
      return filtered.map((det) => {
        const paired = (det.extras || []).find((e) => e.label === '_paired');
        if (!paired) return det;
        const indices = paired.value.split(',').map(Number);
        const updated = indices
          .filter((i) => i !== idx)
          .map((i) => (i > idx ? i - 1 : i));
        if (updated.length === 0) {
          return { ...det, extras: det.extras.filter((e) => e.label !== '_paired') };
        }
        return {
          ...det,
          extras: det.extras.map((e) =>
            e.label === '_paired' ? { ...e, value: updated.join(',') } : e
          ),
        };
      });
    });
  };

  const handleEraseDetection = (idx) => {
    if (!eraseMode) return;
    removeDetection(idx);
  };

  const toggleTool = (tool) => {
    if (activeTool === 'pair' && tool !== 'pair' && pairingDiameterIdx !== null && pairingLengthIndices.length > 0) {
      confirmPairing();
    }
    setActiveTool((t) => t === tool ? null : tool);
    setDrawingRect(null);
    setIsPanning(false);
    if (tool !== 'pair') {
      setPairingDiameterIdx(null);
      setPairingLengthIndices([]);
    }
  };

  const handleSave = performSave;

  const handleDownloadImageWithCrops = () => {
    if (!imgRef.current) return;
    const w = imageSize?.width || imgRef.current.naturalWidth;
    const h = imageSize?.height || imgRef.current.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgRef.current, 0, 0);

    editedDetections.forEach((det) => {
      const color = getColor(det);
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      const pts = det.bbox_points;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.closePath();
      ctx.stroke();
    });

    const link = document.createElement('a');
    link.download = `detections_${analysisHash || 'image'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const handleDownloadFile = async () => {
    if (!analysisHash) return;
    setDownloadError(null);
    try {
      let blob;
      if (mcpPath) {
        try { blob = await downloadFile(mcpPath); } catch { blob = null; }
      }
      if (!blob) {
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const token = localStorage.getItem('token');
        const resp = await fetch(`${apiUrl}/v1/analyses/${analysisHash}/original`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) blob = await resp.blob();
      }
      if (!blob && analysisData?.source === 'mcp') {
        setDownloadSearching(true);
        try {
          const result = await findFileInMcp(analysisHash);
          if (result.found) {
            setMcpPath(result.new_path);
            blob = await downloadFile(result.new_path);
          }
        } finally {
          setDownloadSearching(false);
        }
      }
      if (!blob) {
        const msg = analysisData?.source === 'mcp'
          ? 'No se ha podido encontrar el archivo. Es posible que haya sido eliminado o movido fuera de la colección.'
          : 'No se ha podido encontrar el archivo original.';
        setDownloadError(msg);
        return;
      }
      const filename = analysisData?.filename || `${analysisHash}`;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      console.error('Error downloading file:', e);
      setDownloadError('Error al descargar el archivo.');
    }
  };

  const REPORT_STEPS = [
    'Analizando detecciones del plano...',
    'Estructurando operaciones de mecanizado...',
    'Generando informe...',
  ];

  const handleDownloadExcel = async () => {
    if (!analysisHash || downloadingExcel) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (dirtyRef.current || savingRef.current) {
      try {
        await performSave();
      } catch (_) {
        // performSave already sets saveStatus
      }
      if (dirtyRef.current) {
        alert('No se pudo guardar antes de generar el informe. Inténtalo de nuevo.');
        return;
      }
    }
    setDownloadingExcel(true);
    setReportModal({ open: true, step: 0, done: false, error: false });

    const stepInterval = setInterval(() => {
      setReportModal(prev => {
        if (prev.done || prev.step >= REPORT_STEPS.length - 1) return prev;
        return { ...prev, step: prev.step + 1 };
      });
    }, 8000);

    try {
      await downloadDetectionExcel(analysisHash, page);
      clearInterval(stepInterval);
      setReportModal({ open: true, step: REPORT_STEPS.length - 1, done: true, error: false });
      setTimeout(() => setReportModal({ open: false, step: 0, done: false, error: false }), 2500);
    } catch (err) {
      console.error('Error downloading detection Excel:', err);
      clearInterval(stepInterval);
      setReportModal(prev => ({ ...prev, done: true, error: true }));
    } finally {
      setDownloadingExcel(false);
    }
  };

  const closeReportModal = () => {
    setReportModal({ open: false, step: 0, done: false, error: false });
    setDownloadingExcel(false);
  };

  const handleGoToLocation = useCallback(() => {
    if (!mcpPath) return;
    const folderPath = mcpPath.substring(0, mcpPath.lastIndexOf('/')) || '/';
    const fileName = mcpPath.substring(mcpPath.lastIndexOf('/') + 1);
    window.location.href = `/folder-browser?path=${encodeURIComponent(folderPath)}&highlight=${encodeURIComponent(fileName)}`;
  }, [mcpPath]);

  useImperativeHandle(ref, () => ({
    downloadFile: handleDownloadFile,
    downloadImageWithCrops: handleDownloadImageWithCrops,
    downloadExcel: handleDownloadExcel,
    save: handleSave,
    openHelp: () => setShowHelp(true),
    goToLocation: handleGoToLocation,
  }));

  const hasMcpLocation = analysisData?.source === 'mcp' && !!mcpPath;

  useEffect(() => {
    if (!onStateChange) return;
    onStateChange({
      saving,
      saveStatus,
      hasUnsavedChanges,
      downloadingExcel,
      downloadSearching,
      downloadError,
      detectionsCount: editedDetections.length,
      fromDetectionHistory,
      hasMcpLocation,
      hasAnalysisHash: !!analysisHash,
    });
  }, [onStateChange, saving, saveStatus, hasUnsavedChanges, downloadingExcel, downloadSearching, downloadError, editedDetections.length, fromDetectionHistory, hasMcpLocation, analysisHash]);

  // --- Rendering helpers ---
  const grouped = {};
  editedDetections.forEach((det, idx) => {
    if (!grouped[det.class_name]) grouped[det.class_name] = [];
    grouped[det.class_name].push({ ...det, _idx: idx });
  });
  const sortedClasses = Object.keys(grouped).sort();

  const pointsToSvg = (bboxPoints) =>
    bboxPoints.map(([x, y]) => `${x * scale.x},${y * scale.y}`).join(' ');

  const renderPendingRect = (rect) => {
    const rgb = getRgb(rect.class_name);
    const stroke = `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
    const fill = `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.1)`;
    const x = Math.min(rect.x1, rect.x2);
    const y = Math.min(rect.y1, rect.y2);
    const w = Math.abs(rect.x2 - rect.x1);
    const h = Math.abs(rect.y2 - rect.y1);
    return { x, y, w, h, stroke, fill };
  };

  const zoomPercent = Math.round(zoomLevel * 100);

  return (
    <div className="detection-view">
      {embeddedInPreview ? (
        <div className="detection-meta-bar">
          {analysisData?.filename && (
            <div className="detection-meta-item">
              <span className="detection-meta-label">Archivo:</span>
              <span className="detection-meta-value">{analysisData.filename}</span>
            </div>
          )}
          {analysisData?.status && (
            <div className="detection-meta-item">
              <span className="detection-meta-label">Estado:</span>
              <span className={`detection-meta-value status-${analysisData.status}`}>{analysisData.status}</span>
            </div>
          )}
          {analysisData?.created && (
            <div className="detection-meta-item">
              <span className="detection-meta-label">Fecha:</span>
              <span className="detection-meta-value">{formatMetaDate(analysisData.created)}</span>
            </div>
          )}
          <div className="detection-meta-item">
            <span className="detection-meta-label">Detecciones:</span>
            <span className="detection-meta-value">{editedDetections.length}</span>
          </div>
        </div>
      ) : (
        <div className="detection-header">
          <div className="detection-header-left">
            <h2>Detección de anotaciones</h2>
            <span className="detection-count">{editedDetections.length} detecciones</span>
          </div>
          <div className="detection-header-actions">
            {fromDetectionHistory && (
              <>
                <button className="detection-action-btn" onClick={handleDownloadFile} disabled={downloadSearching} title="Descargar archivo original">
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M8 1v10M4 7l4 4 4-4M2 13h12"/></svg>
                  {downloadSearching ? 'Buscando...' : 'Archivo'}
                </button>
                {downloadError && <span className="download-error-msg">{downloadError}</span>}
                <button className="detection-action-btn" onClick={handleDownloadImageWithCrops} title="Descargar imagen con detecciones">
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="1" y="1" width="14" height="14" rx="2"/><path d="M8 5v6M5 8l3 3 3-3"/></svg>
                  Imagen
                </button>
                {hasMcpLocation && (
                  <button className="detection-action-btn" onClick={handleGoToLocation} title="Ver ubicación del archivo">
                    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 2h5l2 2h5v10H2z"/></svg>
                    Ubicación
                  </button>
                )}
                <button className="detection-action-btn" onClick={onNavigateToPreview} title="Ir a previsualización del análisis">
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                  Preview
                </button>
                <div className="detection-header-separator" />
              </>
            )}
            {analysisHash && (
              <>
                <button className="detection-btn-report" onClick={handleDownloadExcel} disabled={downloadingExcel} title="Generar informe de operaciones">
                  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 1h8v14H4z"/><path d="M6 5h4M6 8h4M6 11h2"/></svg>
                  Generar Informe
                </button>
                <span className={`detection-autosave-status status-${saveStatus || (saving ? 'saving' : (hasUnsavedChanges ? 'dirty' : 'idle'))}`}>
                  {saving && 'Guardando…'}
                  {!saving && saveStatus === 'ok' && '✓ Guardado'}
                  {!saving && saveStatus === 'error' && '⚠ Error al guardar — reintentar'}
                  {!saving && !saveStatus && hasUnsavedChanges && '● Sin guardar'}
                </span>
              </>
            )}
            <button className="detection-btn-help" onClick={() => setShowHelp(true)} title="Ayuda">?</button>
            <button className="detection-btn-back" onClick={onClose}>
              {fromDetectionHistory ? 'Volver a detecciones' : 'Volver al análisis'}
            </button>
          </div>
        </div>
      )}

      <div className="detection-split">
        <div
          className={`detection-image-panel ${activeTool === 'pan' ? (isPanning ? 'panning' : 'pan-ready') : ''}`}
          ref={panelRef}
          onMouseDown={handlePanStart}
          onMouseMove={handlePanMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
        >
          <div className="detection-toolbar">
            <button className="detection-toolbar-btn" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} title="Rotar izquierda">↺</button>
            <button className="detection-toolbar-btn" onClick={() => setRotation((r) => (r + 90) % 360)} title="Rotar derecha">↻</button>
            <div className="detection-toolbar-separator" />
            <button className="detection-toolbar-btn" onClick={handleZoomIn} title="Zoom in">+</button>
            <button className="detection-toolbar-btn" onClick={handleZoomOut} title="Zoom out">−</button>
            <button className="detection-toolbar-btn" onClick={handleFitToScreen} title="Ajustar a pantalla"><IconFit /></button>
            <div className="detection-toolbar-separator" />
            <button className={`detection-toolbar-btn ${activeTool === 'pan' ? 'active' : ''}`} onClick={() => toggleTool('pan')} title="Mover"><IconMoveArrows /></button>
            <button className={`detection-toolbar-btn ${activeTool === 'draw' ? 'active' : ''}`} onClick={() => toggleTool('draw')} title="Dibujar"><IconRectDashed /></button>
            <button className={`detection-toolbar-btn ${activeTool === 'erase' ? 'active' : ''}`} onClick={() => toggleTool('erase')} title="Borrar detección"><IconEraser /></button>
            <button className={`detection-toolbar-btn ${activeTool === 'pair' ? 'active' : ''}`} onClick={() => toggleTool('pair')} title="Emparejar Ø↔L"><IconLink /></button>
            <div className="detection-toolbar-separator" />
            <button className="detection-toolbar-btn" onClick={() => setShowDetections((v) => !v)} title={showDetections ? 'Ocultar detecciones' : 'Mostrar detecciones'}>
              {showDetections ? <IconEye /> : <IconEyeOff />}
            </button>
            <span className="detection-toolbar-zoom">{zoomPercent}%</span>
          </div>

          {pairMode && pairingDiameterIdx !== null && (
            <div className="detection-pair-summary">
              <span>
                Ø{editedDetections[pairingDiameterIdx]?.value || '?'}
                {pairingLengthIndices.length > 0 && (
                  <> ← {pairingLengthIndices.map((i) => `L${editedDetections[i]?.value || '?'}`).join(' + ')}
                  {' = '}{pairingLengthIndices.reduce((sum, i) => {
                    const num = parseFloat((editedDetections[i]?.value || '').replace(',', '.'));
                    return sum + (isNaN(num) ? 0 : num);
                  }, 0)}</>
                )}
              </span>
              {pairingLengthIndices.length > 0 && (
                <button className="detection-pair-confirm" onClick={confirmPairing}>Confirmar</button>
              )}
              <button className="detection-pair-cancel" onClick={() => { setPairingDiameterIdx(null); setPairingLengthIndices([]); }}>×</button>
            </div>
          )}

          {drawingMode && (
            <div className="detection-draw-options">
              <div className="detection-class-picker">
                <span className="detection-picker-chip" style={{ backgroundColor: `rgb(${getRgb(selectedClass).join(',')})` }} />
                <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
                  <option value="Notes">Notes (texto libre)</option>
                  <option disabled>────────────</option>
                  {MEASUREMENT_CLASSES.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
                </select>
              </div>
              {pendingRects.length > 0 && (
                <button className="detection-btn-process" onClick={handleProcessManual} disabled={processingManual}>
                  {processingManual ? 'Procesando...' : `Procesar ${pendingRects.length}`}
                </button>
              )}
            </div>
          )}

          <div
            className={`detection-image-wrapper ${isPanning ? 'panning' : ''}`}
            style={{ transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel}) rotate(${rotation}deg)` }}
          >
            {imageSrc && <img ref={imgRef} src={imageSrc} alt="Plano" onLoad={updateScale} />}
            <svg
              ref={svgRef}
              className={`detection-overlay ${drawingMode ? 'drawing-mode' : ''} ${activeTool === 'pan' ? 'pan-mode' : ''} ${eraseMode ? 'erase-mode' : ''} ${pairMode ? 'pair-mode' : ''}`}
              onMouseDown={handleDrawStart}
              onMouseMove={handleDrawMove}
              onMouseUp={handleDrawEnd}
              onMouseLeave={handleDrawEnd}
            >
              {showDetections && editedDetections.map((det, idx) => {
                const color = getColor(det);
                const isHovered = hoveredIdx === idx;

                let fill = isHovered ? color.replace('rgb', 'rgba').replace(')', ',0.25)') : 'transparent';
                let stroke = color;
                let strokeW = isHovered ? 3 : 2;

                if (pairMode) {
                  if (pairingDiameterIdx === idx) {
                    fill = 'rgba(33,150,243,0.3)';
                    stroke = '#2196F3';
                    strokeW = 3;
                  } else if (pairingLengthIndices.includes(idx)) {
                    fill = 'rgba(76,175,80,0.3)';
                    stroke = '#4CAF50';
                    strokeW = 3;
                  } else if (pairingDiameterIdx !== null && det.class_name !== 'Diameter' && det.class_name !== 'Length') {
                    fill = 'transparent';
                    strokeW = 1;
                  }
                }

                return (
                  <polygon
                    key={idx}
                    points={pointsToSvg(det.bbox_points)}
                    stroke={stroke}
                    strokeWidth={strokeW}
                    fill={fill}
                    style={pairMode ? { opacity: pairingDiameterIdx !== null && det.class_name !== 'Diameter' && det.class_name !== 'Length' ? 0.3 : 1 } : undefined}
                    onClick={() => {
                      if (eraseMode) handleEraseDetection(idx);
                      else if (pairMode) handlePairClick(idx);
                    }}
                    onMouseEnter={() => setHoveredIdx(idx)}
                    onMouseLeave={() => setHoveredIdx(null)}
                  />
                );
              })}

              {pendingRects.map((rect, idx) => {
                const r = renderPendingRect(rect);
                return (
                  <g key={`pending-${idx}`}>
                    <rect x={r.x} y={r.y} width={r.w} height={r.h} stroke={r.stroke} strokeWidth={2} strokeDasharray="6 3" fill={r.fill} />
                    <g className="detection-pending-remove" onClick={(e) => { e.stopPropagation(); removePendingRect(idx); }} style={{ cursor: 'pointer' }}>
                      <circle cx={r.x + r.w} cy={r.y} r={10} fill="#fff" stroke={r.stroke} strokeWidth={1.5} />
                      <text x={r.x + r.w} y={r.y + 4.5} textAnchor="middle" fontSize={13} fontWeight="bold" fill={r.stroke}>×</text>
                    </g>
                  </g>
                );
              })}

              {drawingRect && (() => {
                const r = renderPendingRect(drawingRect);
                return <rect x={r.x} y={r.y} width={r.w} height={r.h} stroke={r.stroke} strokeWidth={2} strokeDasharray="4 2" fill={r.fill} />;
              })()}
            </svg>
          </div>

          {cropPreview && (
            <div className="detection-crop-preview" style={{ borderColor: cropPreview.color }}>
              <img src={cropPreview.src} alt="Crop preview" />
            </div>
          )}
        </div>

        <div className="detection-values-panel">
          {embeddedInPreview && (
            <div className="detection-tabs">
              <button className={`detection-tab ${activeTab === 'annotations' ? 'active' : ''}`} onClick={() => setActiveTab('annotations')}>Anotaciones</button>
              <button className={`detection-tab ${activeTab === 'operations' ? 'active' : ''}`} onClick={() => setActiveTab('operations')}>Operaciones</button>
              <button className={`detection-tab ${activeTab === 'similar' ? 'active' : ''}`} onClick={() => setActiveTab('similar')}>Búsqueda de similares</button>
              <button className={`detection-tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>Información</button>
            </div>
          )}
          {(!embeddedInPreview || activeTab === 'annotations') && (
          <div className="detection-tab-content">
          {sortedClasses.length > 0 && (
            <div className="detection-filter-bar">
              <label className="detection-filter-toggle">
                <input
                  type="checkbox"
                  checked={showOnlyUnmatched}
                  onChange={(e) => setShowOnlyUnmatched(e.target.checked)}
                />
                <span>Ver solo sin asignar</span>
              </label>
            </div>
          )}
          {embeddedInPreview && sortedClasses.length === 0 ? (
            <div className="document-data-center">
              <h2>Detección de anotaciones</h2>
              <p className="no-data-message">Este análisis aún no tiene anotaciones detectadas.</p>
              {onDetectAnnotations && (
                <button
                  className="btn-detect-annotations-large"
                  onClick={onDetectAnnotations}
                  disabled={detectingAnnotations}
                >
                  {detectingAnnotations ? 'Detectando...' : 'Detectar anotaciones'}
                </button>
              )}
            </div>
          ) : sortedClasses.map((className) => {
            const allItems = grouped[className] || [];
            const items = showOnlyUnmatched
              ? allItems.filter((it) => {
                  const matches = (it.extras || []).find((e) => e.label === '_matches')?.value;
                  return !Array.isArray(matches) || matches.length === 0;
                })
              : allItems;
            if (items.length === 0) return null;
            const color = getColor(items[0]);
            return (
              <div key={className} className="detection-class-group">
                <div className="detection-class-header" style={{ borderLeftColor: color }}>
                  <span className="detection-color-chip" style={{ backgroundColor: color }} />
                  <span className="detection-class-name">{className}</span>
                  <span className="detection-class-count">{items.length}</span>
                </div>
                <div className="detection-items">
                  {items.map((det) => {
                    const isPairSelected = pairMode && pairingDiameterIdx === det._idx;
                    const isPairCandidate = pairMode && pairingDiameterIdx !== null && det.class_name === 'Length';
                    const isPairChosen = pairMode && pairingLengthIndices.includes(det._idx);
                    const isPairDimmed = pairMode && pairingDiameterIdx !== null && det.class_name !== 'Diameter' && det.class_name !== 'Length';
                    const pairedSum = det.class_name === 'Diameter' ? getPairedLengthSum(det) : null;
                    const isLengthPaired = det.class_name === 'Length' && editedDetections.some(
                      (d) => (d.extras || []).some((e) => e.label === '_paired' && e.value.split(',').map(Number).includes(det._idx))
                    );

                    return (
                      <div
                        key={det._idx}
                        className={`detection-item ${hoveredIdx === det._idx ? 'hovered' : ''} ${isPairSelected ? 'pair-selected' : ''} ${isPairCandidate ? 'pair-candidate' : ''} ${isPairChosen ? 'pair-chosen' : ''} ${isPairDimmed ? 'pair-dimmed' : ''}`}
                        onMouseEnter={() => { setHoveredIdx(det._idx); handleCropHover(det); }}
                        onMouseLeave={() => { setHoveredIdx(null); setCropPreview(null); }}
                        onClick={pairMode ? () => handlePairClick(det._idx) : undefined}
                      >
                        <select
                          className="detection-class-select"
                          value={det.class_name}
                          onChange={(e) => handleFieldChange(det._idx, 'class_name', e.target.value)}
                          onClick={(e) => pairMode && e.stopPropagation()}
                        >
                          <option value="Notes">Notes</option>
                          <option disabled>──────</option>
                          {MEASUREMENT_CLASSES.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
                        </select>
                        <div className={`detection-item-fields ${det.class_name === 'Notes' ? 'notes-fields' : ''}`} onClick={(e) => pairMode && e.stopPropagation()}>
                          {det.class_name === 'Notes' ? (
                            <textarea
                              className="detection-inline-input notes-value"
                              value={det.value || ''}
                              placeholder="Texto extraído"
                              rows={3}
                              onChange={(e) => handleFieldChange(det._idx, 'value', e.target.value)}
                            />
                          ) : (
                            <input
                              className="detection-inline-input"
                              type="text"
                              value={det.value || ''}
                              placeholder="Valor"
                              onChange={(e) => handleFieldChange(det._idx, 'value', e.target.value)}
                            />
                          )}
                          {(det.extras || []).filter((extra) => !extra.label.startsWith('_')).map((extra, ei) => {
                            const realIdx = (det.extras || []).indexOf(extra);
                            return (
                              <React.Fragment key={ei}>
                                <input className="detection-inline-input extra-label" type="text" value={extra.label} placeholder="Campo" onChange={(e) => handleExtraChange(det._idx, realIdx, 'label', e.target.value)} />
                                <input className="detection-inline-input" type="text" value={extra.value} placeholder="Valor" onChange={(e) => handleExtraChange(det._idx, realIdx, 'value', e.target.value)} />
                                <button className="detection-remove-field" onClick={() => handleRemoveExtra(det._idx, realIdx)}>×</button>
                              </React.Fragment>
                            );
                          })}
                          <button className="detection-add-field" onClick={() => handleAddExtra(det._idx)} title="Añadir campo">+</button>
                        </div>
                        {pairedSum !== null && (
                          <span className="detection-paired-badge" onClick={(e) => e.stopPropagation()}>
                            → L{pairedSum}
                            <button className="detection-paired-remove" onClick={() => {
                              updateDetections((prev) => {
                                const next = [...prev];
                                const d = { ...next[det._idx] };
                                d.extras = (d.extras || []).filter((ex) => ex.label !== '_paired');
                                next[det._idx] = d;
                                return next;
                              });
                            }} title="Quitar emparejamiento">×</button>
                          </span>
                        )}
                        {isLengthPaired && (
                          <span className="detection-linked-dot" title="Emparejado con un diámetro" />
                        )}
                        {det.confidence !== null && det.confidence !== undefined ? (
                          <span className="detection-confidence" title="Confianza de la detección YOLO">{(det.confidence * 100).toFixed(0)}%</span>
                        ) : det.is_manual ? (
                          <span className="detection-manual-badge">Manual</span>
                        ) : null}
                        {det.parse_error && <span className="detection-error" title="Error leyendo valor">⚠</span>}
                        {(() => {
                          const matches = (det.extras || []).find((e) => e.label === '_matches')?.value || [];
                          const hasOverride = !!(det.extras || []).find((e) => e.label === '_match_override');
                          const isAssignable = detectionIsAssignable(det.class_name, enabledOpsList);
                          if (!Array.isArray(matches) || matches.length === 0) {
                            return (
                              <>
                                <span className="detection-unmatched-pill" title="No asignada a ninguna operación"><span className="dot" />Sin asignar</span>
                                {isAssignable && (
                                  <button className="detection-assign-btn" onClick={(e) => { e.stopPropagation(); setEditingDetectionIdx(det._idx); setEditingMatchKey({ op_path: null, instance_idx: null, page: null }); }} title="Asignar manualmente">+ asignar</button>
                                )}
                              </>
                            );
                          }
                          return (
                            <>
                              <span className="detection-matched-pill" title={`${matches.length} match${matches.length > 1 ? 'es' : ''}${hasOverride ? ' (override manual)' : ''}`}>{hasOverride ? '✎ ' : ''}{matches.length} match{matches.length > 1 ? 'es' : ''}</span>
                              {hasOverride && (
                                <button className="detection-clear-override-btn" onClick={(e) => { e.stopPropagation(); removeMatchOverride(det._idx); }} title="Quitar override (volver al automático)">↺</button>
                              )}
                            </>
                          );
                        })()}
                        <button className="detection-delete-item" onClick={(e) => { e.stopPropagation(); removeDetection(det._idx); }} title="Eliminar detección">×</button>
                        {editingDetectionIdx === det._idx && (() => {
                          const overrideList = (() => {
                            const raw = (det.extras || []).find((e) => e.label === '_match_override')?.value;
                            if (Array.isArray(raw)) return raw;
                            if (raw) return [{ ...raw, target_op_path: null, target_instance_idx: null }];
                            return [];
                          })();
                          const currentOverride = editingMatchKey
                            ? overrideList.find((o) =>
                                (o.target_op_path ?? null) === (editingMatchKey.op_path ?? null) &&
                                (o.target_instance_idx ?? null) === (typeof editingMatchKey.instance_idx === 'number' ? editingMatchKey.instance_idx : null)
                              )
                            : null;
                          const matches = (det.extras || []).find((e) => e.label === '_matches')?.value || [];
                          const targetMatch = editingMatchKey
                            ? matches.find((m) =>
                                m && m.op_path === editingMatchKey.op_path &&
                                (typeof m.instance_idx === 'number' ? m.instance_idx : null) === (typeof editingMatchKey.instance_idx === 'number' ? editingMatchKey.instance_idx : null)
                              )
                            : null;
                          return (
                            <div className="detection-match-editor-wrapper">
                              <DetectionMatchEditor
                                detection={det}
                                currentOverride={currentOverride}
                                currentMatch={targetMatch}
                                operacionesMecanizado={operacionesMecanizado}
                                enabledOperations={enabledOpsList}
                                onSave={(override) => upsertMatchOverride(det._idx, override, editingMatchKey)}
                                onCancel={() => { setEditingDetectionIdx(null); setEditingMatchKey(null); }}
                              />
                            </div>
                          );
                        })()}
                        {editingDetectionIdx !== det._idx && (() => {
                          const matches = (det.extras || []).find((e) => e.label === '_matches')?.value || [];
                          if (!Array.isArray(matches) || matches.length === 0) return null;
                          const canEdit = detectionIsAssignable(det.class_name, enabledOpsList);
                          return (
                            <div className="detection-matches-stack">
                              {matches.map((m, mi) => {
                                const key = { op_path: m.op_path, instance_idx: typeof m.instance_idx === 'number' ? m.instance_idx : null, page: m.page ?? null };
                                return (
                                  <DetectionMatchBlock
                                    key={mi}
                                    match={m}
                                    operacionesMecanizado={operacionesMecanizado}
                                    overrideFields={m?._override_fields || null}
                                    onEdit={canEdit ? () => { setEditingDetectionIdx(det._idx); setEditingMatchKey(key); } : null}
                                    onReject={() => rejectMatch(det._idx, m)}
                                  />
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </div>
          )}
          {embeddedInPreview && activeTab === 'operations' && analysisDataState && (
            <div className="detection-tab-content">
              <AnalysisOperations
                analysis={analysisDataWithOverrides}
                selectedPage={page}
                onSelectPage={() => {}}
                showPageSelector={false}
                hideInvoiceFallback
                operationsEnabled={operationsEnabled}
                detections={editedDetections}
              />
            </div>
          )}
          {embeddedInPreview && activeTab === 'similar' && analysisData && (
            <div className="detection-tab-content">
              <SimilarDrawingsPanel
                analysisHash={analysisHash}
                analysisStatus={analysisData?.status}
                onViewAnalysis={onViewSimilarAnalysis || (() => {})}
                drawingPageNumbers={[page]}
              />
            </div>
          )}
          {embeddedInPreview && activeTab === 'info' && analysisData && (
            <div className="detection-tab-content">
              <AnalysisOperations
                analysis={analysisData}
                forceInvoiceView
              />
            </div>
          )}
        </div>
      </div>
      {reportModal.open && (
        <div className="modal-overlay" onClick={reportModal.done ? closeReportModal : undefined}>
          <div className="report-modal" onClick={e => e.stopPropagation()}>
            <div className="report-modal-header">
              <h3>Generando informe</h3>
              {reportModal.done && (
                <button className="modal-close" onClick={closeReportModal}>&times;</button>
              )}
            </div>
            <div className="report-modal-steps">
              {REPORT_STEPS.map((label, i) => {
                const isActive = !reportModal.done && i === reportModal.step;
                const isCompleted = reportModal.done || i < reportModal.step;
                const isPending = !reportModal.done && i > reportModal.step;
                return (
                  <div key={i} className={`report-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isPending ? 'pending' : ''}`}>
                    <span className="report-step-icon">
                      {isCompleted ? '\u2713' : isActive ? <span className="report-spinner" /> : (i + 1)}
                    </span>
                    <span className="report-step-label">{label}</span>
                  </div>
                );
              })}
            </div>
            {reportModal.done && !reportModal.error && (
              <div className="report-modal-success">Informe descargado correctamente</div>
            )}
            {reportModal.error && (
              <div className="report-modal-error">
                Error al generar el informe
                <button className="report-modal-retry" onClick={() => { closeReportModal(); setTimeout(handleDownloadExcel, 100); }}>Reintentar</button>
              </div>
            )}
          </div>
        </div>
      )}
      {showHelp && (
        <div className="modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h3>Ayuda - Detecciones</h3>
              <button className="modal-close" onClick={() => setShowHelp(false)}>&times;</button>
            </div>
            <div className="help-modal-body">
              <div className="help-section">
                <h4>Panel de imagen (izquierda)</h4>
                <p>Muestra el plano con las detecciones marcadas en colores. Cada anotacion detectada tiene un recuadro de color.</p>
                <ul>
                  <li><strong>Rotar</strong> — gira la imagen 90 grados</li>
                  <li><strong>Zoom</strong> — acerca/aleja (tambien con rueda del raton)</li>
                  <li><strong>Mover</strong> — arrastra la imagen para navegar</li>
                  <li><strong>Dibujar</strong> — dibuja un rectangulo sobre una anotacion que no se haya detectado. Selecciona la clase, dibuja el recuadro y pulsa "Procesar" para leer el valor</li>
                  <li><strong>Borrar</strong> — haz click sobre una deteccion en la imagen para eliminarla</li>
                  <li><strong>Emparejar</strong> — vincula un diametro con una o varias longitudes para el informe (ver seccion mas abajo)</li>
                  <li><strong>Ojo</strong> — muestra/oculta los recuadros de detecciones</li>
                </ul>
              </div>
              <div className="help-section">
                <h4>Panel de detecciones (derecha)</h4>
                <p>Lista de todas las detecciones agrupadas por tipo. Cada deteccion es editable:</p>
                <ul>
                  <li><strong>Selector de clase</strong> — cambia el tipo de deteccion (Diameter, Length, Thread...)</li>
                  <li><strong>Valor</strong> — el valor leido de la anotacion. Puedes corregirlo manualmente</li>
                  <li><strong>Campos extra (+)</strong> — anade campos adicionales como tolerancias u otros datos</li>
                  <li><strong>Porcentaje (%)</strong> — confianza del modelo YOLO en la deteccion. Un 95% significa que el modelo esta muy seguro de que ha detectado correctamente ese tipo de anotacion. Valores bajos pueden indicar detecciones dudosas</li>
                  <li><strong>Eliminar (x)</strong> — elimina una deteccion incorrecta</li>
                </ul>
                <p>Al pasar el raton sobre una deteccion, se resalta en la imagen y se muestra una vista ampliada.</p>
              </div>
              <div className="help-section">
                <h4>Clasificacion EXT / INT / N</h4>
                <p>Cada diametro se clasifica automaticamente para el informe. Si no es correcto, puedes forzarlo con los botones a la derecha de cada Diameter:</p>
                <ul>
                  <li><strong>EXT</strong> — diametro exterior</li>
                  <li><strong>INT</strong> — diametro interior (pasante)</li>
                  <li><strong>N</strong> — taladro. Al activarlo aparece un campo para indicar el numero de agujeros</li>
                </ul>
                <p>Pulsa de nuevo el boton activo para desactivarlo y volver a la clasificacion automatica. Guarda antes de generar el informe.</p>
              </div>
              <div className="help-section">
                <h4>Emparejar diametro con longitud</h4>
                <p>El informe necesita saber que longitud (o profundidad) corresponde a cada diametro. Normalmente se detecta automaticamente, pero puedes corregirlo. Funciona para EXT, INT y taladros (profundidad):</p>
                <ol>
                  <li>Pulsa el boton de <strong>enlace</strong> en la barra de herramientas (icono de cadena)</li>
                  <li>Haz click en un <strong>Diameter</strong> (en el panel derecho o en la imagen) — quedara marcado en azul</li>
                  <li>Haz click en una o varias <strong>Length</strong> — se sumaran automaticamente</li>
                  <li>Pulsa <strong>Confirmar</strong> para guardar el emparejamiento</li>
                  <li>Guarda las detecciones antes de generar el informe</li>
                </ol>
                <p>Los diametros emparejados muestran un badge con la longitud total (ej: → L62). Pulsa la × del badge para quitar el emparejamiento.</p>
              </div>
              <div className="help-section">
                <h4>Botones de accion</h4>
                <ul>
                  <li><strong>Guardar</strong> — guarda las detecciones editadas</li>
                  <li><strong>Generar Informe</strong> — genera el Excel con las operaciones de mecanizado a partir de las detecciones</li>
                  <li><strong>Archivo</strong> — descarga el archivo original</li>
                  <li><strong>Imagen</strong> — descarga la imagen con las detecciones dibujadas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

DetectionView.displayName = 'DetectionView';

export default DetectionView;
